import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { translationsApi, userProfileApi } from '../lib/api';

const TranslationContext = createContext(null);

// Ultimate fallback — English is the source of truth in the DB.
const DEFAULT_LANG = 'en';

// Debounce delay for reporting missing keys (5 seconds)
const MISSING_KEY_REPORT_DELAY = 5000;

// localStorage keys — vinylflow_lang keeps the original name for backwards
// compatibility with the previous language selector. The "_explicit" flag is
// what makes the priority chain honour "explicit user choice always wins".
const LS_LANG_KEY = 'vinylflow_lang';
const LS_EXPLICIT_KEY = 'vinylflow_lang_explicit';

// Read the anonymous-visitor language stored in localStorage. Returns
// {lang, isExplicit} — isExplicit=true means the user actively picked
// via the globe selector, so Entry-point defaults must never overwrite it.
const readStoredLanguage = () => {
    try {
        const lang = localStorage.getItem(LS_LANG_KEY);
        const isExplicit = localStorage.getItem(LS_EXPLICIT_KEY) === '1';
        return { lang: lang || null, isExplicit };
    } catch {
        return { lang: null, isExplicit: false };
    }
};

// Return the browser's preferred language reduced to a 2-letter code if
// it is in `supported`. Returns null when no supported match is found.
const detectBrowserLanguage = (supported) => {
    if (typeof navigator === 'undefined') return null;
    const candidates = [
        ...(navigator.languages || []),
        navigator.language,
        navigator.userLanguage,
    ].filter(Boolean);
    for (const raw of candidates) {
        const short = String(raw).toLowerCase().slice(0, 2);
        if (supported.includes(short)) return short;
    }
    return null;
};

export const TranslationProvider = ({ children }) => {
    // Language priority chain (evaluated once at mount, then again on
    // user/company change):
    //   1. Authenticated user's `preferred_language`
    //   2. Anonymous explicit selection in localStorage
    //   3. Active company's `default_language`
    //   4. Entry-point default (Affinity / Campaign / Landing Page)
    //   5. Browser language, if supported
    //   6. English
    //
    // The `isExplicit` state remembers whether the current selection came
    // from priorities 1-2 so Entry-point pages can call
    // `applyEntryPointDefault` without stomping over an explicit choice.
    const initial = readStoredLanguage();
    const [language, setLanguageState] = useState(initial.lang || DEFAULT_LANG);
    const [isExplicit, setIsExplicit] = useState(initial.isExplicit);
    const [translations, setTranslations] = useState({});
    const [loading, setLoading] = useState(true);
    const [supportedLanguages, setSupportedLanguages] = useState(['en']);
    // Latest snapshot of the current user's preferred_language and their
    // active company's default_language. AuthContext pushes these in via
    // `setCurrentUserLanguageContext` whenever `/auth/me` refreshes.
    const [userContext, setUserContext] = useState({
        userLang: null,
        companyLang: null,
        isAuthenticated: false,
    });

    // Missing key tracking for auto-capture
    const missingKeysRef = useRef(new Map()); // Map<key, {defaultMessage, context}>
    const reportTimeoutRef = useRef(null);
    const isAdminRef = useRef(false);
    // Latest entry-point request. Held in a ref so it survives the async
    // fetch of `supportedLanguages` — otherwise a page mounting before the
    // languages list arrives would silently drop its default.
    const pendingEntryPointRef = useRef(null);

    // Set admin status (called from AuthContext)
    const setIsAdmin = useCallback((isAdmin) => {
        isAdminRef.current = isAdmin;
    }, []);

    // Debounced missing key reporter. Open to any authenticated
    // user — the backend sanitises + length-caps + batch-caps the
    // payload (see routes/translations.py). The admin-only gate used
    // to live here, which meant keys first encountered on customer-
    // only screens never auto-registered in Production (admins would
    // not browse those surfaces often). Switched off so customer
    // sessions contribute to key discovery and Danish translations
    // can be entered by admins in the Translation Center.
    const reportMissingKeys = useCallback(async () => {
        if (missingKeysRef.current.size === 0) return;

        const keysToReport = Array.from(missingKeysRef.current.entries()).map(([key, data]) => ({
            key,
            default_message: data.defaultMessage,
            context: data.context,
        }));
        missingKeysRef.current.clear();
        try {
            await translationsApi.reportMissing(keysToReport);
            console.log(`[i18n] Reported ${keysToReport.length} missing keys`);
        } catch (error) {
            // Guest/unauth sessions legitimately 401 — swallow quietly.
            if (error?.response?.status !== 401) {
                console.warn('[i18n] Failed to report missing keys:', error.message);
            }
        }
    }, []);

    // Schedule missing key report (debounced)
    const scheduleMissingKeyReport = useCallback(() => {
        if (reportTimeoutRef.current) clearTimeout(reportTimeoutRef.current);
        reportTimeoutRef.current = setTimeout(reportMissingKeys, MISSING_KEY_REPORT_DELAY);
    }, [reportMissingKeys]);

    // Track a missing key
    const trackMissingKey = useCallback((key, defaultMessage, context) => {
        if (!missingKeysRef.current.has(key)) {
            missingKeysRef.current.set(key, { defaultMessage, context });
            scheduleMissingKeyReport();
        }
    }, [scheduleMissingKeyReport]);

    // Fetch translations for current language
    const fetchTranslations = useCallback(async (lang) => {
        try {
            const response = await translationsApi.getRuntime(lang);
            setTranslations(response.data.translations || {});
        } catch (error) {
            console.error('Failed to fetch translations:', error);
            // Keep existing translations on error → the app renders in whatever
            // language was last loaded (or English defaults inside each t() call).
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch supported languages on mount
    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                const response = await translationsApi.getLanguages();
                setSupportedLanguages(response.data.languages || ['en']);
            } catch (error) {
                console.error('Failed to fetch supported languages:', error);
            }
        };
        fetchLanguages();
    }, []);

    // Fetch translations when language changes
    useEffect(() => {
        fetchTranslations(language);
    }, [language, fetchTranslations]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (reportTimeoutRef.current) {
                clearTimeout(reportTimeoutRef.current);
                reportMissingKeys();
            }
        };
    }, [reportMissingKeys]);

    // Called by AuthContext with the current user's preferred_language and
    // the active company's default_language. Resolves priority 1-3 here so
    // signing in with `preferred_language=da` immediately switches the UI,
    // and signing out reverts to anonymous state.
    //
    // IMPORTANT: read the authoritative "explicit selection" flag from
    // localStorage each time we run — a stale AuthContext user
    // (`user.preferred_language` hasn't refetched yet) must NEVER stomp
    // over a language the visitor just picked in the globe selector.
    // `setLanguage` writes localStorage synchronously before returning, so
    // the store is our single source of truth here.
    const setCurrentUserLanguageContext = useCallback((next) => {
        const userLang = next?.preferred_language || null;
        const companyLang = next?.company_default_language || null;
        const isAuthenticated = !!next?.isAuthenticated;
        setUserContext({ userLang, companyLang, isAuthenticated });

        const stored = readStoredLanguage();

        if (isAuthenticated && userLang && supportedLanguages.includes(userLang)) {
            // Priority 1 — the server-side preferred_language on this
            // user record. Only apply when it actually differs from what
            // localStorage says, so a just-clicked explicit choice waiting
            // to be persisted to /auth/me is not overwritten. Once the
            // /auth/me refresh completes, userLang matches localStorage
            // and this branch is a no-op.
            if (userLang !== stored.lang) {
                setLanguageState(userLang);
                setIsExplicit(true);
                try {
                    localStorage.setItem(LS_LANG_KEY, userLang);
                    localStorage.setItem(LS_EXPLICIT_KEY, '1');
                } catch {}
            } else {
                // Already in sync — just make sure the explicit flag is set.
                setIsExplicit(true);
            }
            return;
        }

        // Auth'd user without an explicit preferred_language →
        // priority 3 (company default) applies IF no anonymous explicit
        // choice was previously stored.
        if (isAuthenticated && !userLang && !stored.isExplicit) {
            if (companyLang && supportedLanguages.includes(companyLang)) {
                setLanguageState(companyLang);
                setIsExplicit(false);
                try {
                    localStorage.setItem(LS_LANG_KEY, companyLang);
                    localStorage.removeItem(LS_EXPLICIT_KEY);
                } catch {}
            }
        }
    }, [supportedLanguages]);

    // Public API — an entry-point page (Affinity register, Campaign,
    // Landing Page) calls this with its `default_language`. It only takes
    // effect when the visitor has no higher-priority language, i.e.:
    //   - not authenticated with a preferred_language, AND
    //   - no anonymous explicit selection in localStorage
    // In every other case it is a no-op (Priority 3-5 never overrides 1-2).
    //
    // Handles the async `supportedLanguages` race by parking the request
    // in a ref until the languages list arrives (see effect below).
    const applyEntryPointDefault = useCallback((entryLang) => {
        if (!entryLang) return;
        const short = String(entryLang).toLowerCase().slice(0, 2);
        // Priority checks (evaluated with current state; browser-load race
        // is handled by re-running via `pendingEntryPointRef`).
        if (isExplicit) return;
        if (userContext.isAuthenticated && userContext.userLang) return;
        if (userContext.isAuthenticated && userContext.companyLang) return;

        if (!supportedLanguages.includes(short)) {
            // Languages list not loaded yet — remember and retry.
            if (supportedLanguages.length <= 1) {
                pendingEntryPointRef.current = short;
            }
            return;
        }
        if (short === language) return;
        setLanguageState(short);
        setIsExplicit(false);
        try {
            localStorage.setItem(LS_LANG_KEY, short);
            localStorage.removeItem(LS_EXPLICIT_KEY);
        } catch {}
    }, [supportedLanguages, isExplicit, userContext, language]);

    // Once we know the supported list, apply Priority 5 (browser) for
    // anonymous first-time visitors who have no localStorage entry at all.
    // Also flushes any Entry-point request that arrived before the list.
    useEffect(() => {
        if (supportedLanguages.length <= 1) return; // still loading

        // Flush a queued entry-point default first (Priority 4 ≻ 5).
        const queued = pendingEntryPointRef.current;
        if (queued && supportedLanguages.includes(queued)) {
            pendingEntryPointRef.current = null;
            const stored = readStoredLanguage();
            if (!stored.isExplicit
                && !(userContext.isAuthenticated && (userContext.userLang || userContext.companyLang))) {
                if (queued !== language) {
                    setLanguageState(queued);
                    setIsExplicit(false);
                    try {
                        localStorage.setItem(LS_LANG_KEY, queued);
                        localStorage.removeItem(LS_EXPLICIT_KEY);
                    } catch {}
                }
                return; // Entry-point wins over browser fallback.
            }
        }

        const stored = readStoredLanguage();
        if (stored.lang) return; // Priority 1-4 already handled elsewhere
        if (userContext.isAuthenticated) return;
        const browser = detectBrowserLanguage(supportedLanguages);
        if (browser && browser !== language) {
            setLanguageState(browser);
            setIsExplicit(false);
        }
    }, [supportedLanguages, userContext.isAuthenticated, userContext.userLang, userContext.companyLang, language]);

    // User (auth'd or anon) picks a language via the globe selector.
    // Always marks the choice as explicit. Persists to localStorage; if
    // authenticated, also PATCHes /auth/me so the choice roams across
    // devices / sessions.
    const setLanguage = useCallback(async (lang) => {
        const short = String(lang || '').toLowerCase().slice(0, 2);
        if (!supportedLanguages.includes(short)) return;

        setLanguageState(short);
        setIsExplicit(true);
        try {
            localStorage.setItem(LS_LANG_KEY, short);
            localStorage.setItem(LS_EXPLICIT_KEY, '1');
        } catch {}

        if (userContext.isAuthenticated) {
            try {
                await userProfileApi.updateSelf({ preferred_language: short });
                // Keep the local copy in sync so re-render of the header
                // reflects the just-saved value without a /me refetch.
                setUserContext((prev) => ({ ...prev, userLang: short }));
            } catch (error) {
                console.warn('[i18n] Failed to persist preferred_language:', error?.message);
                // localStorage still holds the choice, so the UX stays in
                // the picked language for this session. Server will catch
                // up on the next successful update.
            }
        }
    }, [supportedLanguages, userContext.isAuthenticated]);

    // Translation function with English fallback and auto-capture of missing keys.
    const t = useCallback((key, defaultMessageOrParams, paramsOrContext, contextArg) => {
        let defaultMessage = '';
        let params = {};
        let context = '';

        if (typeof defaultMessageOrParams === 'string') {
            defaultMessage = defaultMessageOrParams;
            if (typeof paramsOrContext === 'object' && paramsOrContext !== null) {
                params = paramsOrContext;
                context = contextArg || '';
            } else if (typeof paramsOrContext === 'string') {
                context = paramsOrContext;
            }
        } else if (typeof defaultMessageOrParams === 'object' && defaultMessageOrParams !== null) {
            params = defaultMessageOrParams;
        }

        let text = translations[key];

        if (text === undefined || text === '') {
            text = defaultMessage || key;
            if (defaultMessage && isAdminRef.current && language !== 'en') {
                trackMissingKey(key, defaultMessage, context);
            }
        }

        if (params && typeof params === 'object') {
            Object.entries(params).forEach(([param, value]) => {
                text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), value);
            });
        }

        return text;
    }, [translations, language, trackMissingKey]);

    // Refresh translations (useful after admin updates)
    const refreshTranslations = () => fetchTranslations(language);

    const value = {
        language,
        setLanguage,
        t,
        translations,
        loading,
        supportedLanguages,
        refreshTranslations,
        setIsAdmin,
        // New API surface for Priority chain integration:
        isExplicitLanguage: isExplicit,
        setCurrentUserLanguageContext,
        applyEntryPointDefault,
    };

    return (
        <TranslationContext.Provider value={value}>
            {children}
        </TranslationContext.Provider>
    );
};

export const useTranslation = () => {
    const context = useContext(TranslationContext);
    if (!context) {
        throw new Error('useTranslation must be used within a TranslationProvider');
    }
    return context;
};
